import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { AddUpdateMeetingModal } from '@/modules/common/components/modals/AddUpdateMeetingModal.tsx';
import { MeetingItemFullInfoModal } from '@/modules/common/components/modals/MeetingItemFullInfoModal.tsx';
import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';
import { utcIsoToMeetingDateTime } from '@/modules/common/helpers/meetingDateTime.ts';
import {
  useDeleteMeetingMutation,
  useSendMeetingReminderMutation,
} from '@/modules/common/queries/meetings.ts';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import {
  Badge,
  Button,
  DisclosureListItem,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  getSelectValueByEnum,
} from 'ui';
import {
  CameraWhiteIcon,
  DotsHorizontalIcon,
  EditGrayIcon,
  SendGreyIcon,
  TrashGrayIcon,
  UserManagementWhiteIcon,
} from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import { ScheduledMeetingListItem } from 'compliance-api';

export interface MeetingItemProps {
  meeting: ScheduledMeetingListItem;
}

const formatUtcOffsetLabel = (totalOffsetMinutes: number) => {
  const sign = totalOffsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(totalOffsetMinutes);
  const hours = Math.floor(absoluteOffsetMinutes / 60);
  const minutes = absoluteOffsetMinutes % 60;

  return `GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatDisplayedRange = (startDateTime: dayjs.Dayjs, endDateTime: dayjs.Dayjs) => {
  const startLabel = startDateTime.format('DD-MM-YYYY, HH:mm');
  const endLabel = endDateTime.isSame(startDateTime, 'day')
    ? endDateTime.format('HH:mm')
    : endDateTime.format('DD-MM-YYYY, HH:mm');

  return `${startLabel} - ${endLabel}`;
};

export const getMeetingTimeZoneSummary = (
  startTime: string,
  endTime: string,
  timeZone: string,
  options?: { includeGmt?: boolean },
) => {
  const timeZoneStart = utcIsoToMeetingDateTime(startTime, timeZone);
  const timeZoneEnd = utcIsoToMeetingDateTime(endTime, timeZone);
  const timeZoneName = getSelectValueByEnum<Timezone>(timeZone, Timezone, false).label;
  const offsetLabel = formatUtcOffsetLabel(timeZoneStart.utcOffset());
  const timezoneLabel =
    options?.includeGmt === false ? timeZoneName : `${timeZoneName}, ${offsetLabel}`;

  return `${formatDisplayedRange(timeZoneStart, timeZoneEnd)} (${timezoneLabel})`;
};

export const getMeetingLocalTimeSummary = (
  startTime: string,
  endTime: string,
  options?: { includeGmt?: boolean; timeZone?: string },
) => {
  const localStart = dayjs(startTime);
  const localEnd = dayjs(endTime);
  const timeZoneStart = utcIsoToMeetingDateTime(startTime, options?.timeZone);
  const timeZoneEnd = utcIsoToMeetingDateTime(endTime, options?.timeZone);
  const offsetLabel = formatUtcOffsetLabel(localStart.utcOffset());
  const localLabel = options?.includeGmt === false ? 'Local' : `Local, ${offsetLabel}`;

  const isSameAsMeetingTime =
    localStart.format('DD-MM-YYYY HH:mm') === timeZoneStart.format('DD-MM-YYYY HH:mm') &&
    localEnd.format('DD-MM-YYYY HH:mm') === timeZoneEnd.format('DD-MM-YYYY HH:mm');

  if (isSameAsMeetingTime) {
    return null;
  }

  return `${formatDisplayedRange(localStart, localEnd)} (${localLabel})`;
};

export const MeetingItem = ({ meeting }: MeetingItemProps) => {
  const userRole = rolesManipulator.current_user_role;
  const isAdmin = userRole === Role.COMPLIANCE_ADMIN;
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: sendMeetingReminder, isLoading: isSendingMeetingReminder } =
    useSendMeetingReminderMutation();

  const handleOpenDeleteModal = useCallback(() => {
    openModal(DeleteModal, {
      name: meeting.subject,
      id: meeting.id,
      title: 'meeting',
      mutation: useDeleteMeetingMutation,
    });
  }, [openModal, meeting.id, meeting.subject]);

  const handleOpenEditModal = useCallback(() => {
    openModal(AddUpdateMeetingModal, {
      item: meeting,
    });
  }, [openModal, meeting]);

  const handleOpenFull = useCallback(() => {
    openModal(MeetingItemFullInfoModal, {
      id: meeting.id,
    });
  }, [openModal, meeting.id]);

  const handleSendReminder = useCallback(async () => {
    await sendMeetingReminder({ meetingId: meeting.id });
  }, [meeting.id, sendMeetingReminder]);

  const items = useMemo<DropdownMenuItem[]>(() => {
    const items: DropdownMenuItem[] = [
      {
        label: 'Remind attendees',
        onClick: handleSendReminder,
        icon: SendGreyIcon,
        disabled: isSendingMeetingReminder,
      },
      { label: 'Edit', onClick: handleOpenEditModal, icon: EditGrayIcon },
      { label: 'Delete', onClick: handleOpenDeleteModal, icon: TrashGrayIcon },
    ];

    return items;
  }, [handleOpenDeleteModal, handleOpenEditModal, handleSendReminder, isSendingMeetingReminder]);

  return (
    <DisclosureListItem
      onClick={handleOpenFull}
      leftMainString={meeting.subject}
      leftSubduedContentSet={
        [
          getMeetingTimeZoneSummary(meeting.start_time, meeting.end_time, meeting.time_zone, {
            includeGmt: false,
          }),
          getMeetingLocalTimeSummary(meeting.start_time, meeting.end_time, {
            includeGmt: false,
            timeZone: meeting.time_zone,
          }),
        ].filter(Boolean) as string[]
      }
      classes={{
        leftContent: 'w-1/2',
        rightContentUp: 'w-1/2 max-w-[450px]',
        leftMainString: 'font-medium text-gray-900',
      }}
      rightContent={
        <div className="flex gap-6 items-center justify-between w-full">
          <div className="flex flex-col">
            <span className="text-xs leading-5 font-[Inter] text-gray-500">Attendees</span>
            <span>
              {(meeting.attendee_users?.length ?? 0) +
                (meeting?.external_attendee_emails?.length ?? 0)}
            </span>
          </div>

          <BadgeOnline isOnline={meeting.is_online_meeting} addPadding={!meeting.join_url} />

          <ButtonCopyLink link={meeting.join_url} />

          {isAdmin && (
            <DropdownMenu
              items={items}
              button={
                <img
                  src={DotsHorizontalIcon}
                  alt="More..."
                  className="w-6 h-6 min-w-6 min-h-6 hover:opacity-80 cursor-pointer"
                />
              }
              containerClassName="w-5 h-5 min-w-5 min-h-5"
              className="w-[220px] py-0"
              placement={'bottom-end'}
              iconItemsWithBorder={false}
            />
          )}
        </div>
      }
    />
  );
};

interface BadgeOnlineProps {
  isOnline?: boolean;
  addPadding?: boolean;
}
export const BadgeOnline = ({ isOnline, addPadding = false }: BadgeOnlineProps) => {
  if (!isOnline) {
    return (
      <div
        className={classNames('w-fit font-[Inter]', {
          'm-0 mr-auto pl-[26px]': addPadding,
        })}
      >
        <Badge className="bg-[#00a5ff] text-white rounded-xl py-0 h-[24px] flex items-center gap-2 px-3">
          <img src={UserManagementWhiteIcon} alt="In Person" className="min-h-4 h-4 min-w-4 w-4" />{' '}
          In Person
        </Badge>
      </div>
    );
  }

  return (
    <div
      className={classNames('w-fit font-[Inter]', {
        'm-0 mr-auto pl-[26px]': addPadding,
      })}
    >
      <Badge className="bg-[#0369A1] text-white rounded-xl py-0 h-[24px] flex items-center gap-2 px-3">
        <img src={CameraWhiteIcon} alt="Online" className="min-h-4 h-4 min-w-4 w-4" /> Online
      </Badge>
    </div>
  );
};

interface ButtonCopyLinkProps {
  link?: string;
  color?: 'secondary' | 'primary';
}
export const ButtonCopyLink = ({ link, color = 'secondary' }: ButtonCopyLinkProps) => {
  const handleCopyLink = () => {
    copyTextToClipBoard(String(link ?? ''), {
      successMessage: 'Meeting link copied successfully',
    });
  };

  if (!link) {
    return null;
  }

  return (
    <div className="w-fit font-[Inter]" onClick={e => e.stopPropagation()}>
      <Button variant={color} onClick={handleCopyLink}>
        Copy join link
      </Button>
    </div>
  );
};
